import React from 'react';

import {
  Avatar,
  Badge,
  Button,
  Center,
  Checkbox,
  Flex,
  HStack,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { RiArrowRightSLine } from 'react-icons/ri';

import {
  IconChevronDown,
  IconFlag,
  IconYellowStar,
  formatDate,
} from '@/components';
import { DataListCell, DataListRow, DataListText } from '@/components/DataList';
import { fullScopePaginationPageSizes } from '@/components/Pagination/FullScopePagination';
import { LinkAdmin } from '@/features/admin';
import { InterviewStatus } from '@/services/api';
import { getFullname } from '@/utils/identity';
import {
  parseAsInteger,
  parseAsNumberLiteral,
  useQueryState,
} from '@/utils/useQueryState';

import { statusIcon } from '../../components/statusIcon';
import { statusColor } from '../interview/statusColor';
import { statusLabel } from '../interview/statusLabel';
import { CandidateOptions } from './CandidateOptions';
import { type CandidateListItemProps } from './types';
import { useCandidateLink } from './useCandidateLink';
import {
  useOdiBotStartMutation,
  useOdiBotStopMutation,
} from './useCandidateTableMutation';

export const CandidateListItems: React.FC<CandidateListItemProps> = ({
  candidate,
  isSelected,
  onSelect,
  isShowAiScore,
  variant = 'dashboard',
  participantIndex,
}) => {
  const { t } = useTranslation(['common', 'interview', 'candidate', 'survey']);

  const [page] = useQueryState(
    'page',
    parseAsInteger.withDefault(1).withOptions({
      clearOnDefault: true,
    })
  );

  const [pageSize] = useQueryState(
    'pageSize',
    parseAsNumberLiteral(fullScopePaginationPageSizes)
      .withDefault(10)
      .withOptions({ clearOnDefault: true })
  );

  const getDisplayName = () => {
    if (variant === 'survey' && participantIndex !== undefined) {
      const displayNumber = (page - 1) * pageSize + participantIndex + 1;
      return `Anon.${displayNumber}`;
    }
    return getFullname(candidate.name, candidate.surname);
  };

  const candidateLink = useCandidateLink(candidate.interviewId, candidate.id);
  const odiBotStartMutation = useOdiBotStartMutation();
  const odiBotStopMutation = useOdiBotStopMutation();

  const interviewAssistantHandler = () => {
    if (candidate.status === InterviewStatus.InTheInterview) {
      // Stop the interview
      odiBotStopMutation.mutate({
        interviewId: candidate.interviewId,
        candidateId: candidate.id,
      });
      return;
    }

    if (candidate.status === InterviewStatus.Added) {
      // Start the interview
      odiBotStartMutation.mutate({
        interviewId: candidate.interviewId,
        candidateId: candidate.id,
      });
    }
  };

  return (
    <DataListRow withHover>
      <DataListCell w="10" p={0} alignItems="center" borderRight="none">
        <Checkbox
          isChecked={isSelected}
          onChange={(e) => onSelect(candidate, e.target.checked)}
        />
      </DataListCell>

      <DataListCell
        alignItems="center"
        flexDir="row"
        gap={2}
        py={2}
        px={3}
        flex={2}
      >
        <Avatar
          size="sm"
          name={getDisplayName()}
          bg="brand.50"
          textColor="brand.500"
        />
        <LinkAdmin minW={0} w="full" to={candidateLink.toString()}>
          <Flex direction="column">
            <DataListText display="flex" columnGap={2}>
              {getDisplayName()}{' '}
              {candidate.isRedFlag && (
                <Tooltip
                  hasArrow
                  openDelay={100}
                  bg="black"
                  borderRadius="md"
                  wordBreak="normal"
                  whiteSpace="nowrap"
                  maxWidth="none"
                  label={t('candidate:redFlagDescription')}
                  px={2}
                  py={1}
                >
                  <Center
                    rounded="full"
                    flexShrink={0}
                    bg="error.50"
                    py={0.5}
                    px={1.5}
                  >
                    <HStack>
                      <IconFlag fontSize="12px" />
                      <IconChevronDown fontSize="12px" color="error.500" />
                    </HStack>
                  </Center>
                </Tooltip>
              )}
            </DataListText>
            <DataListText noOfLines={1} fontSize="sm" fontWeight="light">
              {candidate.email}
            </DataListText>
            <DataListText noOfLines={1} fontSize="sm" fontWeight="light">
              {candidate.phone}
            </DataListText>
            <DataListText noOfLines={1} fontSize="sm" fontWeight="light">
              {candidate.identityNumber}
            </DataListText>
          </Flex>
        </LinkAdmin>
      </DataListCell>

      <DataListCell>
        {candidate.createdAt && (
          <DataListText color="text-dimmed" fontSize="sm">
            {formatDate(candidate.createdAt)}
          </DataListText>
        )}
      </DataListCell>
      <DataListCell align="center">
        <Badge colorScheme={statusColor(candidate.status).schema}>
          <HStack gap={1}>
            {statusIcon(candidate.status!)}
            <Text>{statusLabel(candidate.status)}</Text>
          </HStack>
        </Badge>
      </DataListCell>
      <DataListCell>
        <DataListText color="text-dimmed" alignSelf="center">
          {candidate.rating} <IconYellowStar />
        </DataListText>
      </DataListCell>
      <DataListCell w="12%" align="center">
        <DataListText color="text-dimmed" alignSelf="center">
          {isShowAiScore ? (
            <CandidateRating
              isLoading={candidate.reAnalyzing}
              aiRating={candidate.aiRating}
            />
          ) : (
            '-'
          )}
        </DataListText>
      </DataListCell>
      <DataListCell p={0} w="18%" flexDir="row">
        <DataListCell alignItems="center" p={0} flex={1}>
          {candidate.interview?.type === 'InterviewAssistant' ? (
            <Text
              onClick={interviewAssistantHandler}
              fontWeight="bold"
              cursor="pointer"
              color={
                candidate.status === InterviewStatus.InTheInterview
                  ? 'error.600'
                  : 'primary.500'
              }
            >
              {candidate.status === InterviewStatus.InTheInterview &&
                t('candidate:inviteNewCandidate.finishInterview')}

              {candidate.status === InterviewStatus.Added &&
                t('candidate:inviteNewCandidate.startInterview')}

              {candidate.status === InterviewStatus.Completed && (
                <LinkAdmin to={candidateLink.toString()}>
                  <Button
                    h="9"
                    fontSize="sm"
                    rightIcon={<RiArrowRightSLine size="20px" />}
                    variant="@secondary"
                  >
                    {t('common:detail.detail')}
                  </Button>
                </LinkAdmin>
              )}
            </Text>
          ) : (
            <LinkAdmin to={candidateLink.toString()}>
              <Button
                h="9"
                fontSize="sm"
                rightIcon={<RiArrowRightSLine size="20px" />}
                variant="@secondary"
              >
                {t('common:detail.detail')}
              </Button>
            </LinkAdmin>
          )}
        </DataListCell>
        <DataListCell p={0} h="full" w="14" align="center">
          <CandidateOptions
            candidate={candidate}
            interviewId={candidate.interviewId}
          />
        </DataListCell>
      </DataListCell>
    </DataListRow>
  );
};

interface CandidateRatingProps {
  isLoading?: boolean;
  aiRating?: number;
}

const CandidateRating: React.FC<CandidateRatingProps> = ({
  isLoading,
  aiRating,
}) => {
  return (
    <Flex
      borderRadius="full"
      bgColor="brand.50"
      px={3}
      py={1.5}
      fontSize="sm"
      fontWeight="medium"
      textColor="brand.700"
      whiteSpace="nowrap"
      pos="relative"
    >
      {isLoading && (
        <Spinner
          pos="absolute"
          left={0}
          right={0}
          bottom={0}
          top={0}
          marginX="auto"
          marginY="auto"
          size="sm"
        />
      )}

      <Text fontWeight="bold" visibility={isLoading ? 'hidden' : undefined}>
        {Math.round(aiRating ?? 0)}
      </Text>
      <Text visibility={isLoading ? 'hidden' : undefined}>/ 100</Text>
    </Flex>
  );
};
